import { __decorate, __param, __metadata } from 'tslib';
import { Inject } from '@tencent/ioc';
import SchematicUIService from '../../controllers/base/schematic-ui-service.js';
import { StateManager } from '../store/state-manager.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { Service } from '../../decorators/class/service.js';
import { $logger } from '../../controllers/logger/logger.js';
import { ServiceNames } from '../../constants/service-name.js';
import { EventEmitter } from '../event-emitter/event-emitter.js';
import { InternalEventEmitter } from '../event-emitter/internal-event-emitter.js';
import { InternalEvent } from '../event-emitter/internal-event.js';
import { updateSchemaByKey } from './util.js';

const initialMenu = [
    { name: 'divider', content: 'divider' },
    { name: 'none', content: 'none' },
];
const reserved = initialMenu.map(item => item.name);
/**
 * 用于管理菜单项目的服务
 *
 * @public
 */
let MenuService = class MenuService extends SchematicUIService {
    constructor(store, internalEventEmitter) {
        super();
        this.store = store;
        this.internalEventEmitter = internalEventEmitter;
        this.preventDismissSelectors = [];
        this.schema = [];
        this.relations = new Map();
        this.store.register(BuiltinUiState.Menu, {
            name: 'root', content: 'root', children: [],
        });
        // 注册自带配置
        initialMenu.forEach((internalConfig) => {
            this.configs.set(internalConfig.name, internalConfig);
        });
    }
    $forceUpdate() {
        this.forceUpdateWithStore(this.store, BuiltinUiState.Menu);
    }
    onUpdate() {
        super.onUpdate();
        const root = { name: 'root', content: 'root', children: [] };
        this.relations.clear();
        this.schema.reduce(this.readSchema.bind(this, []), root);
        this.store.get(BuiltinUiState.Menu).next(root);
        this.internalEventEmitter.emit(InternalEvent.SchemaRead, ServiceNames.MENU);
    }
    mergeSchemaWithoutUpdate(schema) {
        this.schema = this.schema.concat(schema);
    }
    /**
     * 向菜单管理器注册菜单
     *
     * @param config - 菜单配置
     * @param updateImmediately - 是否立刻更新
     */
    register(config, updateImmediately = true) {
        if (reserved.includes(config.name)) {
            $logger.warn(`The menu config "${config.name}" is reserved.`);
            return;
        }
        super.register(config, updateImmediately);
        if (typeof config.preventDismiss === 'string') {
            this.preventDismissSelectors.push([config.name, config.preventDismiss]);
        }
    }
    /**
     * 将 schema 和 config 进行匹配整理
     *
     * @internal
     */
    readSchema(paths, root, item) {
        var _a;
        const parent = Object.assign({}, root);
        if (parent.name !== 'root') {
            paths.push(parent.name);
        }
        if (typeof item === 'string' && this.hasConfig(item)) {
            const config = Object.assign(Object.assign({}, this.get(item)), { children: [] });
            this.setPathToActionInfoInConfig(config, paths);
            parent.children.push(config);
        }
        else if (typeof item !== 'string' && this.hasConfig(item.key)) {
            const childItem = Object.assign(Object.assign({}, this.get(item.key)), { showType: (_a = item.type) !== null && _a !== void 0 ? _a : 'vertical', children: [] });
            this.setPathToActionInfoInConfig(childItem, paths);
            item.items.reduce((previous, current) => this.readSchema([...paths], previous, current), childItem);
            parent.children.push(childItem);
        }
        this.relations.set(parent.name, parent.children.map(child => child.name));
        return parent;
    }
    /**
     * 获取某个特定 config
     *
     * @internal
     *
     * @param key - config 的 name
     */
    get(key) {
        return super.get(key, {
            name: 'none',
            content: 'none',
            onClick() {
                // do nothing
            },
        });
    }
    /**
     * 获取子元素到祖先元素的路径
     *
     * @param descendant - 子元素 key
     * @param ancestor - 祖先元素 key
     */
    path(descendant, ancestor) {
        if (!ancestor) {
            return this.relations.has(descendant) ? [descendant] : [];
        }
        if (!this.relations.has(ancestor)) {
            return [];
        }
        const list = [];
        this.find(ancestor, descendant, list);
        return list;
    }
    /**
     * 查找 schema 配置表的子元素，修改子元素的内容，返回新的配置表
     *
     * @param key - 子元素 key
     * @param updateItems - 替换的配置元素
     */
    updateKeySchema(key, updateItems, shouldUpdate = false) {
        const preSchema = this.getSchema();
        const newSchema = updateSchemaByKey(preSchema, key, updateItems);
        if (shouldUpdate) {
            this.setSchema(newSchema);
        }
        return newSchema;
    }
    find(node, target, list) {
        var _a;
        const children = (_a = this.relations.get(node)) !== null && _a !== void 0 ? _a : [];
        if (children.includes(target)) {
            list.unshift(target);
            return true;
        }
        for (const child of children) {
            if (this.find(child, target, list)) {
                list.unshift(child);
                return true;
            }
        }
        return false;
    }
};
MenuService = __decorate([
    Service({ overridable: true, scope: 'ui:service', name: ServiceNames.MENU }),
    __param(1, Inject(InternalEventEmitter)),
    __metadata("design:paramtypes", [StateManager, EventEmitter])
], MenuService);

export { MenuService };
//# sourceMappingURL=menu-service.js.map
